import { useId, type TextareaHTMLAttributes } from "react";
import { FieldMarker } from "@/components/FieldMarker";
import {
  FIELD_ERROR,
  FIELD_LABEL,
  FIELD_LABEL_ERROR,
  FIELD_TEXTAREA,
} from "@/components/fieldStyles";

// The multi-line sibling of TextField (BRK-S412): the same label, marker,
// inline error and aria wiring, on the shared field styles. Extracted when the
// account Actions menu's three dialogs each needed one.
interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  /** Show a muted "(optional)" marker (the counterpart to the required "*"). */
  optional?: boolean;
}

export function TextAreaField({
  label,
  error,
  id,
  optional,
  rows = 3,
  ...areaProps
}: TextAreaFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  return (
    <div className="flex w-full flex-col gap-1 text-left">
      <span className="flex items-baseline gap-1.5">
        <label
          htmlFor={fieldId}
          className={error ? FIELD_LABEL_ERROR : FIELD_LABEL}
        >
          {label}
        </label>
        <FieldMarker required={areaProps.required} optional={optional} />
      </span>
      <textarea
        id={fieldId}
        rows={rows}
        className={FIELD_TEXTAREA}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...areaProps}
      />
      {error && (
        <p id={errorId} className={FIELD_ERROR}>
          {error}
        </p>
      )}
    </div>
  );
}
